import React, { useEffect, useState } from "react";
import { Hourglass, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const SYM = { BRL: "R$", USD: "US$", EUR: "€" };
const money = (a, c) => (a != null && c ? `${SYM[c] || c} ${Number(a).toFixed(2)}` : "—");
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

export function PendingAlertsCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/pending-check").then(({ data }) => setInfo(data)).catch(() => setInfo({ error: true }));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pending-check/run?force=true");
      toast.success(data.count ? `Aviso com ${data.count} pagamento(s) pendente(s) enviado para ${data.to}` : "Nenhum pagamento pendente há mais de 48h.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Falha ao enviar o aviso"); }
    finally { setBusy(false); }
  };
  if (!info) return null;
  const last = info.history?.[0];
  return (
    <div className="glass rounded-2xl p-5" data-testid="pending-alerts-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-display text-lg text-xmas-snow"><Hourglass className="h-5 w-5 text-xmas-gold" /> Pagamentos pendentes (Monetizze)</p>
          <p className="mt-1 text-xs text-xmas-champagne" data-testid="pending-alerts-info">
            Verificação {info.schedule?.toLowerCase()} · aviso quando boleto/Pix fica pendente por mais de {info.threshold_hours}h · para <span className="text-xmas-gold">{info.recipient || "—"}</span>
            {!info.email_enabled && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-300">envio real só em produção</span>}
          </p>
          <p className="mt-1 text-[11px] text-xmas-slate" data-testid="pending-alerts-count">
            Pendentes há mais de {info.threshold_hours}h agora: <span className={`font-bold ${info.stale_count ? "text-amber-300" : "text-emerald-300"}`}>{info.stale_count}</span>{info.stale_unalerted ? ` (${info.stale_unalerted} ainda sem aviso)` : ""}
            {last ? ` · última verificação: ${fmt(last.at)} · ${last.count} pendente(s)${last.emailed ? " · e-mail enviado" : last.reason === "nothing_pending" ? "" : ` · não enviado (${last.reason || last.error || "—"})`}` : ""}
          </p>
        </div>
        <button onClick={run} disabled={busy || !info.recipient} data-testid="pending-alerts-run" className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-xmas-snow transition hover:border-xmas-gold hover:text-xmas-gold disabled:opacity-50">
          <RefreshCw className="h-3.5 w-3.5" /> {busy ? "Verificando…" : "Verificar e enviar agora"}
        </button>
      </div>
      {info.stale?.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-xs" data-testid="pending-alerts-table">
            <thead className="text-[10px] uppercase tracking-wider text-xmas-slate"><tr><th className="py-1.5 pr-3">País</th><th className="pr-3">Valor</th><th className="pr-3">Forma</th><th className="pr-3">Comprador</th><th className="pr-3">Pendente há</th><th>Avisado</th></tr></thead>
            <tbody>
              {info.stale.slice(0, 8).map((p) => (
                <tr key={p.transaction_id} className="border-t border-white/5 text-xmas-snow" data-testid={`pending-row-${p.transaction_id}`}>
                  <td className="py-1.5 pr-3">{p.market}</td><td className="pr-3">{money(p.amount, p.currency)}</td><td className="pr-3">{p.payment_method || "—"}</td>
                  <td className="pr-3 text-xmas-slate">{p.buyer_email}</td><td className="pr-3 text-amber-300">{p.hours_pending}h</td><td className="text-xmas-slate">{p.alerted_at ? fmt(p.alerted_at) : "não"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {info.stale.length > 8 && <p className="mt-1 text-[11px] text-xmas-slate">+{info.stale.length - 8} outro(s)</p>}
        </div>
      )}
    </div>
  );
}
